// GDPR consent banner. Shown when no decision is stored. Calls
// gtag('consent', 'update', ...) on click and persists the choice in
// localStorage so the banner doesn't reappear.
//
// The default consent state (denied) is set in index.html — that script
// also reads localStorage at boot and grants consent if the user previously
// accepted. So this component is only visible to users who haven't decided.

import { useEffect, useState } from "react";

const STORAGE_KEY = "gdpr_consent_v1";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export default function ConsentBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setShow(true);
    } catch {
      /* localStorage blocked (private browsing); show banner anyway */
      setShow(true);
    }
  }, []);

  function accept() {
    try { localStorage.setItem(STORAGE_KEY, "granted"); } catch {}
    if (window.gtag) {
      window.gtag("consent", "update", {
        ad_storage:         "granted",
        ad_user_data:       "granted",
        ad_personalization: "granted",
        analytics_storage:  "granted",
      });
    }
    setShow(false);
  }

  function reject() {
    try { localStorage.setItem(STORAGE_KEY, "denied"); } catch {}
    if (window.gtag) {
      window.gtag("consent", "update", {
        ad_storage:         "denied",
        ad_user_data:       "denied",
        ad_personalization: "denied",
        analytics_storage:  "denied",
      });
    }
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1100,
        background: "rgba(14, 27, 45, 0.97)",
        borderTop: "1px solid var(--border)",
        backdropFilter: "blur(8px)",
        padding: "0.9rem 1.2rem",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: "16rem" }}>
          <p style={{ margin: 0, fontSize: "0.9rem" }}>
            We use cookies for anonymous usage analytics (Google Analytics 4).
            Nothing is collected until you accept.
          </p>
          {showDetails && (
            <p
              className="muted"
              style={{ margin: "0.4rem 0 0", fontSize: "0.8rem" }}
            >
              "Accept" enables Google Analytics page-view tracking and Google
              Consent Mode v2 signals (ad_storage, ad_user_data,
              ad_personalization, analytics_storage). "Reject" keeps all of
              those denied — Google still receives a pingback that counts as
              "consent denied" traffic, but no cookies are set. Your wallet
              address is never sent to Google.{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  try {
                    localStorage.removeItem(STORAGE_KEY);
                  } catch {}
                  location.reload();
                }}
              >
                Reset choice
              </a>
            </p>
          )}
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="ghost"
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              color: "var(--text-dim)",
              fontSize: "0.8rem",
              marginTop: "0.2rem",
              cursor: "pointer",
            }}
          >
            {showDetails ? "Hide details" : "What does this track?"}
          </button>
        </div>
        <div className="row" style={{ gap: "0.5rem" }}>
          <button className="ghost" onClick={reject}>
            Reject
          </button>
          <button onClick={accept}>Accept</button>
        </div>
      </div>
    </div>
  );
}
